import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRightLeft,
    BookOpen,
    CheckCircle2,
    Copy,
    FileSearch,
    Globe,
    Gavel,
    Hammer,
    Loader2,
    MessageSquare,
    RefreshCw,
    Scale,
    ShieldAlert,
    Sparkles,
    Table2,
    Triangle,
    Upload,
    Zap,
} from "lucide-react";
import { toast } from "sonner";
import { ekspertizHasar } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AracKontekstBar from "./ekspertiz-hasar/AracKontekstBar";
import EkspertizHasarModulGiris from "./ekspertiz-hasar/EkspertizHasarModulGiris";
import EhmStickyOzet from "./ekspertiz-hasar/EhmStickyOzet";
import ParcaPiyasaPanel from "./ekspertiz-hasar/ParcaPiyasaPanel";

const TABS = [
    { id: "komuta", label: "Komuta", icon: Zap },
    { id: "uzman", label: "Hasar Uzmanı", icon: Scale },
    { id: "sunum", label: "Sunum Raporu", icon: BookOpen },
    { id: "adli", label: "Adli Paket", icon: Gavel },
    { id: "dosyalar", label: "Dosyalar", icon: FileSearch },
    { id: "whatsapp", label: "WhatsApp", icon: MessageSquare },
];

/** TR TL: önce API *_fmt, yoksa sayıyı 1.234.567,89 biçiminde gösterir */
function tl(n, fmt) {
    if (fmt) return fmt;
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    if (Math.abs(v) < 0.005) return "—";
    const whole = Math.abs(v - Math.round(v)) < 0.01;
    if (whole) return `₺${Math.round(v).toLocaleString("tr-TR")}`;
    const [main, dec] = v.toFixed(2).split(".");
    return `₺${Number(main).toLocaleString("tr-TR")},${dec}`;
}

function pct(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function apiHataMesaji(e, varsayilan = "İşlem başarısız") {
    const ham = e?.response?.data?.error || e?.response?.data?.detail || e?.message || "";
    if (/timeout|timed out|ETIMEDOUT|504|Gateway/i.test(String(ham))) {
        return "İşlem çok uzun sürdü (sunucu zaman aşımı). Hızlı tarama kullanıldı; tam analiz için tekrar deneyin.";
    }
    if (/timeout of \d+ms/i.test(String(ham))) {
        return "İstek zaman aşımına uğradı. Lütfen tekrar deneyin veya daha az parçalı belge yükleyin.";
    }
    return ham || varsayilan;
}

function Kpi({ label, value, hint, accent = "gold" }) {
    const ring =
        accent === "red"
            ? "border-rose-500/30 bg-rose-500/5"
            : accent === "emerald"
              ? "border-emerald-500/30 bg-emerald-500/5"
              : accent === "sky"
                ? "border-sky-500/30 bg-sky-500/5"
                : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-xl border p-4 ${ring}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display text-2xl text-white mt-1">{value ?? "—"}</div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1">{hint}</div> : null}
        </div>
    );
}

function UyumsuzlukKart({ u }) {
    const renk =
        u.seviye === "kritik"
            ? "border-rose-500/40 bg-rose-500/10 text-rose-200"
            : u.seviye === "uyari"
              ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
              : "border-sky-500/30 bg-sky-500/10 text-sky-200";
    return (
        <div className={`rounded-lg border p-3 text-sm ${renk}`}>
            <div className="font-medium flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                {u.baslik}
            </div>
            <p className="mt-1 text-xs opacity-90">{u.mesaj}</p>
        </div>
    );
}

function BelgeKart({ baslik, analiz, etiket, onRemove }) {
    if (!analiz) return null;
    return (
        <div className="rounded-xl border border-white/10 bg-[#121A2F]/60 p-4">
            <div className="flex justify-between items-start mb-3">
                <div>
                    <h3 className="text-sm font-medium text-white">{baslik}</h3>
                    {etiket ? (
                        <span className="text-[10px] text-slate-500">{etiket}</span>
                    ) : null}
                </div>
                {onRemove ? (
                    <button
                        type="button"
                        className="text-xs text-slate-500 hover:text-rose-400"
                        onClick={onRemove}
                    >
                        Kaldır
                    </button>
                ) : null}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                    <span className="text-slate-500">Toplam</span>
                    <div className="text-emerald-300 font-medium">
                        {tl(analiz.toplam_tutar, analiz.toplam_tutar_fmt)}
                    </div>
                </div>
                <div>
                    <span className="text-slate-500">Parça</span>
                    <div className="text-white">{analiz.parca_sayisi ?? analiz.parcalar?.length ?? 0}</div>
                </div>
            </div>
            {analiz.tespit_ozeti ? (
                <p className="text-xs text-slate-400 mb-2">{analiz.tespit_ozeti}</p>
            ) : null}
        </div>
    );
}

export default function EkspertizHasarMerkezPage() {
    const [searchParams] = useSearchParams();
    const deepDosyaId = searchParams.get("dosya");
    const [tab, setTab] = useState("komuta");
    const [loading, setLoading] = useState(false);
    const [ozet, setOzet] = useState(null);
    const [dosyalar, setDosyalar] = useState([]);
    const [seciliDosya, setSeciliDosya] = useState(null);
    const [ekspertiz, setEkspertiz] = useState(null);
    const [fatura, setFatura] = useState(null);
    const [sigortaTeklif, setSigortaTeklif] = useState(null);
    const [gelismis, setGelismis] = useState(null);
    const [sunum, setSunum] = useState(null);
    const [adliPaket, setAdliPaket] = useState(null);
    const [dkOnizle, setDkOnizle] = useState(null);
    const [waKuyruk, setWaKuyruk] = useState([]);
    const [parcaTarama, setParcaTarama] = useState(false);
    const [aracMarka, setAracMarka] = useState("");
    const [aracModel, setAracModel] = useState("");
    const [aracYil, setAracYil] = useState("");

    const payloadBase = (opts = {}) => ({
        ekspertiz,
        fatura,
        sigorta_teklif: sigortaTeklif,
        sigorta_dosyasi_id: seciliDosya?.id,
        dosya_id: seciliDosya?.id,
        marka: aracMarka || undefined,
        model: aracModel || undefined,
        model_yili: aracYil ? Number(aracYil) : undefined,
        parca_ara: opts.parca_ara !== false,
        web_ara: opts.web_ara !== false,
        hizli: opts.hizli ? "1" : "0",
    });

    const aracSenkron = useCallback((kaynak) => {
        if (!kaynak) return;
        if (kaynak.marka) setAracMarka(kaynak.marka);
        if (kaynak.model) setAracModel(kaynak.model);
        if (kaynak.model_yili) setAracYil(String(kaynak.model_yili));
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [o, d] = await Promise.all([ekspertizHasar.ozet(), ekspertizHasar.dosyalar()]);
            setOzet(o);
            setDosyalar(d.dosyalar || []);
            if (tab === "whatsapp") {
                const w = await ekspertizHasar.whatsappKuyruk();
                setWaKuyruk(w.kuyruk || []);
            }
        } catch {
            toast.error("Veri yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (tab === "whatsapp") {
            ekspertizHasar.whatsappKuyruk().then((w) => setWaKuyruk(w.kuyruk || [])).catch(() => {});
        }
    }, [tab]);

    const uploadBelge = async (file, tip, setter) => {
        if (!file) return;
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("belge_tipi", tip);
            if (seciliDosya?.id) fd.append("dosya_id", String(seciliDosya.id));
            const r = await ekspertizHasar.analiz(fd);
            if (r.success === false || r.error) throw new Error(r.error || "Analiz başarısız");
            if (!r.analiz) throw new Error("Belge okunamadı");
            setter(r.analiz);
            aracSenkron(r.analiz);
            aracSenkron(seciliDosya);
            toast.success(
                r.belge_etiket || (tip === "ekspertiz_raporu" ? "Ekspertiz okundu" : "Fatura okundu")
            );
            if (r.rapor_baslik) toast.info(r.rapor_baslik, { duration: 4000 });
            setTab("uzman");
            await gelismisAnalizCalistir(r.analiz, tip === "ekspertiz_raporu" ? "eks" : "fat", { hizli: true });
        } catch (e) {
            toast.error(apiHataMesaji(e, "Analiz başarısız"));
        } finally {
            setLoading(false);
        }
    };

    const gelismisAnalizCalistir = async (yeniBelge, tip, opts = {}) => {
        const eks = opts.ekspertiz ?? (tip === "eks" ? yeniBelge : ekspertiz);
        const fat = opts.fatura ?? (tip === "fat" ? yeniBelge : fatura);
        if (!eks && !fat) return;
        if (eks?.marka) aracSenkron(eks);
        const hizli = Boolean(opts.hizli);
        setParcaTarama(true);
        try {
            const r = await ekspertizHasar.gelismisUcgen({
                ...payloadBase({ hizli, web_ara: !hizli, parca_ara: true }),
                ekspertiz: eks,
                fatura: fat,
            });
            if (r.success === false) throw new Error(r.error || "Analiz başarısız");
            setGelismis(r);
            if (r.arac_kontekst) aracSenkron(r.arac_kontekst);
            if (r.piyasa_ozet?.taranan_parca) {
                toast.success(
                    hizli
                        ? `Hızlı tarama: ${r.piyasa_ozet.taranan_parca} parça — tam analiz için butona basın`
                        : `Parça piyasa taraması: ${r.piyasa_ozet.taranan_parca} kalem`
                );
            } else if (r.parca_piyasa?.hata) {
                toast.warning(r.parca_piyasa.hata);
            }
        } catch (e) {
            toast.error(apiHataMesaji(e, "Gelişmiş analiz başarısız"));
        } finally {
            setParcaTarama(false);
        }
    };

    const gelismisAnaliz = async () => {
        if (!ekspertiz && !fatura) {
            toast.error("En az ekspertiz veya fatura yükleyin");
            return;
        }
        if (!aracMarka.trim()) {
            toast.error("Parça taraması için marka girin veya dosya seçin");
            return;
        }
        setLoading(true);
        setParcaTarama(true);
        try {
            const r = await ekspertizHasar.gelismisUcgen(payloadBase({ hizli: false, web_ara: true }));
            if (r.success === false) throw new Error(r.error);
            setGelismis(r);
            if (r.arac_kontekst) aracSenkron(r.arac_kontekst);
            toast.success(r.piyasa_ozet?.mesaj || "Gelişmiş analiz tamamlandı");
        } catch (e) {
            toast.error(apiHataMesaji(e, "Analiz yapılamadı"));
        } finally {
            setLoading(false);
            setParcaTarama(false);
        }
    };

    const sunumOlustur = async () => {
        if (!ekspertiz && !fatura) {
            toast.error("Önce belge yükleyin");
            return;
        }
        setLoading(true);
        try {
            const r = await ekspertizHasar.sunumRapor({ ...payloadBase(), web_ara: true });
            setSunum(r);
            setGelismis(r.gelismis_analiz || gelismis);
            setTab("sunum");
            toast.success("Sunum raporu hazır");
        } catch (e) {
            toast.error(e?.response?.data?.error || "Rapor oluşturulamadı");
        } finally {
            setLoading(false);
        }
    };

    const adliPaketCalistir = async () => {
        if (!seciliDosya?.id) {
            toast.error("Adli paket için dosya seçin");
            setTab("dosyalar");
            return;
        }
        if (!ekspertiz && !fatura) {
            toast.error("En az bir belge yükleyin");
            return;
        }
        setLoading(true);
        try {
            const r = await ekspertizHasar.adliPaket({ ...payloadBase(), dk_onizle: true });
            setAdliPaket(r);
            setSunum(r.sunum_raporu);
            setGelismis(r.sunum_raporu?.gelismis_analiz);
            setDkOnizle(r.deger_kaybi_onizleme);
            setTab("adli");
            toast.success(r.paket_baslik || "Adli paket hazır");
        } catch (e) {
            toast.error(e?.response?.data?.error || "Paket oluşturulamadı");
        } finally {
            setLoading(false);
        }
    };

    const kopyala = (metin, label) => {
        if (!metin) return;
        navigator.clipboard.writeText(metin).then(
            () => toast.success(`${label} kopyalandı`),
            () => toast.error("Kopyalanamadı")
        );
    };

    const dkHesapOnizle = async () => {
        if (!seciliDosya?.id) {
            toast.error("Dosya seçin");
            return;
        }
        setLoading(true);
        try {
            const r = await ekspertizHasar.dkOnizle(payloadBase());
            setDkOnizle(r.hesap);
            toast.success("DK önizleme (kaydetmez)");
        } catch (e) {
            toast.error(e?.response?.data?.error || "Önizleme yapılamadı");
        } finally {
            setLoading(false);
        }
    };

    const dkAktar = async () => {
        if (!seciliDosya?.id) return;
        if (!window.confirm("Değer kaybı dosyaya kaydedilsin mi?")) return;
        setLoading(true);
        try {
            const r = await ekspertizHasar.dkAktar({ ...payloadBase(), onay: true });
            toast.success(`Kaydedildi — ${tl(r.hesap?.gercek_deger_kaybi)}`);
            setDkOnizle(r.hesap);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Aktarım başarısız");
        } finally {
            setLoading(false);
        }
    };

    const dosyaEvraklariniOtomatikYukle = async (birlesik) => {
        const evraklar = birlesik.evraklar || [];
        const eksper = evraklar.find((e) => e.evrak_turu === "EKSPER");
        const faturaEvrak = evraklar.find((e) => {
            if (e.evrak_turu === "EKSPER" || !e.analiz_uygun) return false;
            const lbl = (e.evrak_label || "").toLowerCase();
            return (
                lbl.includes("fatura") ||
                lbl.includes("hasar") ||
                lbl.includes("onarım") ||
                lbl.includes("onarim") ||
                e.evrak_turu === "DIGER_EVRAK"
            );
        });

        let eks = null;
        let fat = null;

        if (eksper) {
            try {
                const r = await ekspertizHasar.analizEvrak(eksper.id, "ekspertiz_raporu");
                eks = r.analiz;
                setEkspertiz(eks);
            } catch (e) {
                toast.warning(apiHataMesaji(e, "Ekspertiz evrak okunamadı"));
            }
        }

        if (faturaEvrak) {
            try {
                const r = await ekspertizHasar.analizEvrak(faturaEvrak.id, "hasar_faturasi");
                fat = r.analiz;
                setFatura(fat);
            } catch {
                /* fatura opsiyonel */
            }
        }

        if (eks?.marka) aracSenkron(eks);
        else aracSenkron(birlesik);

        if (eks || fat) {
            await gelismisAnalizCalistir(eks || fat, eks ? "eks" : "fat", {
                hizli: true,
                ekspertiz: eks,
                fatura: fat,
            });
            toast.success(
                fat
                    ? "Dosya evrakları yüklendi (ekspertiz + fatura)"
                    : "Ekspertiz yüklendi — fatura yok, karşılaştırma eksik kalır",
            );
        } else if (evraklar.length) {
            toast.info("Evrak var ama otomatik okunamadı — Dosyalar sekmesinden Analiz'e basın");
        } else {
            toast.info("Dosyada evrak yok — ekspertiz/fatura yükleyin");
        }
    };

    const dosyaSec = async (d) => {
        if (!d) {
            setSeciliDosya(null);
            setEkspertiz(null);
            setFatura(null);
            setGelismis(null);
            return;
        }
        setSeciliDosya(d);
        aracSenkron(d);
        setLoading(true);
        try {
            const det = await ekspertizHasar.dosyaDetay(d.id);
            const birlesik = { ...d, ...det.dosya, evraklar: det.evraklar };
            setSeciliDosya(birlesik);
            aracSenkron(birlesik);
            await dosyaEvraklariniOtomatikYukle(birlesik);
        } catch {
            toast.error("Dosya detayı alınamadı");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!deepDosyaId || !dosyalar.length) return;
        const id = Number(deepDosyaId);
        const d = dosyalar.find((x) => x.id === id);
        if (d && seciliDosya?.id !== id) {
            dosyaSec(d);
            setTab("uzman");
        }
    }, [deepDosyaId, dosyalar, seciliDosya?.id]);

    const evrakAnaliz = async (evrak_id, tip) => {
        setLoading(true);
        try {
            const r = await ekspertizHasar.analizEvrak(evrak_id, tip);
            if (tip === "ekspertiz_raporu") setEkspertiz(r.analiz);
            else setFatura(r.analiz);
            toast.success("Evrak analiz edildi");
            setTab("uzman");
            await gelismisAnalizCalistir(r.analiz, tip === "ekspertiz_raporu" ? "eks" : "fat", { hizli: true });
        } catch (e) {
            toast.error(apiHataMesaji(e, "Evrak analizi başarısız"));
        } finally {
            setLoading(false);
        }
    };

    const ucgen = gelismis?.ucgen;
    const satirlar = gelismis?.satir_karsilastirma || [];
    const web = gelismis?.web_istihbarat;
    const piyasaOzet = gelismis?.piyasa_ozet;
    const aracKontekst = gelismis?.arac_kontekst;

    return (
        <div className="p-6 max-w-[90rem] mx-auto space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl text-white flex items-center gap-2">
                        <Hammer className="h-7 w-7 text-[#D4AF37]" />
                        Hasar Uzmanı Merkezi
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-3xl">
                        Parça bazlı internet piyasa taraması (marka/model/yıl) · ekspertiz–fatura–sigorta
                        üçgeni · örnek ilanlar · hukuk sunum raporu.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link
                        to="/yeni/rayic-parca-merkezi/hasar/satin-alma"
                        className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline px-3 py-2 rounded-lg border border-[#D4AF37]/30"
                    >
                        Hasar Satın Alma →
                    </Link>
                    <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                        )}
                        Yenile
                    </Button>
                </div>
            </div>

            {!seciliDosya ? (
                <EkspertizHasarModulGiris onBasla={() => setTab("uzman")} />
            ) : null}

            {seciliDosya ? (
                <div className="rounded-lg border border-sky-500/20 bg-sky-500/5 px-4 py-2 text-xs text-sky-200/90 flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                        Evrak: {(seciliDosya.evraklar || []).filter((e) => e.evrak_turu === "EKSPER").length ? "✓ Ekspertiz" : "✗ Ekspertiz yok"}
                    </span>
                    <span>
                        {ekspertiz ? "✓ Okundu" : "— Okunmadı"}
                        {fatura ? " · ✓ Fatura" : " · ✗ Fatura yok"}
                    </span>
                    {!fatura && ekspertiz ? (
                        <span className="text-amber-300">Fatura yüklenince E↔F fark tablosu dolar</span>
                    ) : null}
                </div>
            ) : null}

            <div className="flex flex-wrap gap-2 border-b border-white/5 pb-3">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                                tab === t.id
                                    ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
                                    : "text-slate-400 hover:text-white hover:bg-white/5"
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "komuta" && ozet ? (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <Kpi label="Aktif dosya" value={ozet.aktif_dosya} />
                        <Kpi label="Ekspertiz evrak" value={ozet.ekspertiz_evrak} accent="emerald" />
                        <Kpi label="DK hesaplı" value={ozet.dk_hesapli} />
                        <Kpi label="Analiz adayı" value={ozet.analiz_aday_dosya} accent="sky" />
                        <Kpi label="WA medya (30g)" value={ozet.whatsapp_son_30_gun} accent="red" />
                    </div>
                    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-5 text-sm text-slate-300 space-y-2">
                        <p>
                            <strong className="text-[#D4AF37]">Önerilen akış:</strong> Dosya seç →
                            ekspertiz + fatura yükle → <em>Hasar Uzmanı</em> otomatik web taraması →{" "}
                            <em>Sunum Raporu</em> veya tek tık <em>Adli Paket</em>.
                        </p>
                        <p className="text-xs text-slate-500">
                            Rapor başlıkları otomatik oluşturulur (dosya no + plaka + tarih).
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Button className="bg-[#D4AF37] text-black" onClick={() => setTab("uzman")}>
                            Hasar Uzmanı <ArrowRightLeft className="h-4 w-4 ml-2" />
                        </Button>
                        <Button variant="outline" onClick={() => setTab("adli")}>
                            <Gavel className="h-4 w-4 mr-2" />
                            Adli Paket
                        </Button>
                    </div>
                </div>
            ) : null}

            {tab === "uzman" ? (
                <div className="space-y-6">
                    <EhmStickyOzet
                        seciliDosya={seciliDosya}
                        ucgen={ucgen}
                        loading={loading}
                        parcaTarama={parcaTarama}
                        onTamAnaliz={gelismisAnaliz}
                        onSunum={sunumOlustur}
                    />

                    <AracKontekstBar
                        arac={aracKontekst}
                        marka={aracMarka || seciliDosya?.marka || ""}
                        model={aracModel || seciliDosya?.model || ""}
                        modelYili={aracYil || (seciliDosya?.model_yili ? String(seciliDosya.model_yili) : "")}
                        onMarka={setAracMarka}
                        onModel={setAracModel}
                        onModelYili={setAracYil}
                        seciliDosya={seciliDosya}
                    />

                    <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-white/10 bg-[#121A2F]/40">
                        <span className="text-sm text-slate-400">Dosya:</span>
                        {seciliDosya ? (
                            <Link
                                to={seciliDosya.link || `/e/sigorta-dosyalari/${seciliDosya.id}`}
                                className="text-[#D4AF37] text-sm font-medium"
                            >
                                {seciliDosya.dosya_no}
                            </Link>
                        ) : (
                            <span className="text-amber-400 text-sm">Dosyalar sekmesinden seçin</span>
                        )}
                        <Button
                            size="sm"
                            className="bg-violet-600 hover:bg-violet-700 ml-auto"
                            onClick={gelismisAnaliz}
                            disabled={loading}
                        >
                            <Globe className="h-4 w-4 mr-1" />
                            Parça piyasa + üçgen
                        </Button>
                        <Button size="sm" variant="outline" onClick={sunumOlustur} disabled={loading}>
                            <BookOpen className="h-4 w-4 mr-1" />
                            Sunum hazırla
                        </Button>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-4">
                        <label className="rounded-xl border border-dashed border-[#D4AF37]/30 p-5 hover:border-[#D4AF37]/60 cursor-pointer">
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={(e) =>
                                    uploadBelge(e.target.files?.[0], "ekspertiz_raporu", setEkspertiz)
                                }
                            />
                            <Upload className="h-7 w-7 text-[#D4AF37] mb-2" />
                            <div className="text-white font-medium text-sm">Ekspertiz raporu</div>
                            <div className="text-[10px] text-slate-500">AI parça + tutar çıkarımı</div>
                        </label>
                        <label className="rounded-xl border border-dashed border-emerald-500/30 p-5 hover:border-emerald-500/60 cursor-pointer">
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={(e) =>
                                    uploadBelge(e.target.files?.[0], "hasar_faturasi", setFatura)
                                }
                            />
                            <Upload className="h-7 w-7 text-emerald-400 mb-2" />
                            <div className="text-white font-medium text-sm">Onarım faturası</div>
                            <div className="text-[10px] text-slate-500">Gerçek hasar tutarı</div>
                        </label>
                        <label className="rounded-xl border border-dashed border-sky-500/30 p-5 hover:border-sky-500/60 cursor-pointer">
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={(e) =>
                                    uploadBelge(e.target.files?.[0], "sigorta_teklif", setSigortaTeklif)
                                }
                            />
                            <Upload className="h-7 w-7 text-sky-400 mb-2" />
                            <div className="text-white font-medium text-sm">Sigorta teklifi</div>
                            <div className="text-[10px] text-slate-500">Önerilen onarım (opsiyonel)</div>
                        </label>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-4">
                        <BelgeKart
                            baslik="Ekspertiz"
                            etiket={gelismis?.rapor_baslik}
                            analiz={ekspertiz}
                            onRemove={() => setEkspertiz(null)}
                        />
                        <BelgeKart baslik="Fatura" analiz={fatura} onRemove={() => setFatura(null)} />
                        <BelgeKart
                            baslik="Sigorta teklifi"
                            analiz={sigortaTeklif}
                            onRemove={() => setSigortaTeklif(null)}
                        />
                    </div>

                    {ucgen ? (
                        <div className="space-y-4">
                            <div className="grid sm:grid-cols-4 gap-3">
                                <Kpi
                                    label="Ekspertiz"
                                    value={tl(ucgen.ekspertiz_toplam, ucgen.ekspertiz_toplam_fmt)}
                                />
                                <Kpi
                                    label="Fatura"
                                    value={tl(ucgen.fatura_toplam, ucgen.fatura_toplam_fmt)}
                                    accent="emerald"
                                />
                                <Kpi
                                    label="Sigorta öneri"
                                    value={tl(
                                        ucgen.sigorta_onerilen_toplam,
                                        ucgen.sigorta_onerilen_toplam_fmt
                                    )}
                                    accent="sky"
                                />
                                <Kpi
                                    label="Uyumluluk"
                                    value={`%${ucgen.uyumluluk_skoru}`}
                                    accent={ucgen.uyumluluk_skoru >= 70 ? "emerald" : "red"}
                                />
                            </div>
                            <p className="text-sm text-slate-400">{ucgen.ozet}</p>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {ucgen.uyumsuzluklar?.map((u, i) => (
                                    <UyumsuzlukKart key={i} u={u} />
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {satirlar.length ? (
                        <div className="rounded-xl border border-white/10 overflow-hidden">
                            <div className="px-4 py-3 border-b border-white/10 text-sm text-white flex items-center gap-2">
                                <Table2 className="h-4 w-4 text-[#D4AF37]" />
                                Net fark tablosu — parça bazlı
                            </div>
                            <div className="overflow-x-auto max-h-80">
                                <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-[#0f172a]">
                                        <tr className="text-left text-slate-500">
                                            <th className="p-2">Parça</th>
                                            <th className="p-2">Ekspertiz</th>
                                            <th className="p-2">Fatura</th>
                                            <th className="p-2">Sigorta</th>
                                            <th className="p-2">E↔F fark</th>
                                            <th className="p-2">S↔F fark</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {satirlar.map((s, i) => (
                                            <tr
                                                key={i}
                                                className={`border-t border-white/5 ${
                                                    s.islem_uyumsuz ? "bg-rose-500/5" : ""
                                                }`}
                                            >
                                                <td className="p-2 text-white">
                                                    {s.parca_adi}
                                                    {s.islem_uyumsuz ? (
                                                        <AlertTriangle className="inline h-3 w-3 text-rose-400 ml-1" />
                                                    ) : null}
                                                </td>
                                                <td className="p-2 text-slate-400">
                                                    {tl(s.ekspertiz_tutar, s.ekspertiz_tutar_fmt)}
                                                </td>
                                                <td className="p-2 text-emerald-300">
                                                    {tl(s.fatura_tutar, s.fatura_tutar_fmt)}
                                                </td>
                                                <td className="p-2 text-sky-300">
                                                    {tl(s.sigorta_tutar, s.sigorta_tutar_fmt)}
                                                </td>
                                                <td
                                                    className={`p-2 ${
                                                        (s.fark_ekspertiz_fatura || 0) > 0
                                                            ? "text-rose-300"
                                                            : "text-slate-400"
                                                    }`}
                                                >
                                                    {s.fark_ekspertiz_fatura != null
                                                        ? tl(
                                                              s.fark_ekspertiz_fatura,
                                                              s.fark_ekspertiz_fatura_fmt
                                                          )
                                                        : "—"}
                                                </td>
                                                <td className="p-2 text-amber-300">
                                                    {s.fark_sigorta_fatura != null
                                                        ? tl(
                                                              s.fark_sigorta_fatura,
                                                              s.fark_sigorta_fatura_fmt
                                                          )
                                                        : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    <ParcaPiyasaPanel
                        satirlar={satirlar}
                        piyasaOzet={piyasaOzet}
                        parcaPiyasa={gelismis?.parca_piyasa}
                        aracKontekst={aracKontekst}
                        loading={parcaTarama}
                    />

                    {web?.sonuclar?.length ? (
                        <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-4">
                            <h3 className="text-sm text-violet-200 flex items-center gap-2 mb-3">
                                <Globe className="h-4 w-4" />
                                Web istihbarat ({web.sonuc_sayisi} sonuç)
                                {web.piyasa_medyan_ipucu ? (
                                    <span className="text-xs text-slate-400 ml-auto">
                                        Medyan ipucu{" "}
                                        {tl(
                                            web.piyasa_medyan_ipucu,
                                            web.piyasa_medyan_ipucu_fmt
                                        )}
                                    </span>
                                ) : null}
                            </h3>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {web.sonuclar.slice(0, 8).map((w, i) => (
                                    <div key={i} className="text-xs border-b border-white/5 pb-2">
                                        {w.url ? (
                                            <a
                                                href={w.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-violet-300 hover:underline"
                                            >
                                                {w.baslik}
                                            </a>
                                        ) : (
                                            <span className="text-slate-300">{w.baslik}</span>
                                        )}
                                        <p className="text-slate-500 mt-0.5">{w.ozet}</p>
                                    </div>
                                ))}
                            </div>
                            {gelismis?.web_kyas?.fark_yuzde != null ? (
                                <p className="text-xs text-amber-200/90 mt-3">
                                    {gelismis.web_kyas.yorum} (fark {pct(gelismis.web_kyas.fark_yuzde)})
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="rounded-xl border border-white/10 p-4 flex flex-wrap gap-2">
                        <Button variant="outline" onClick={dkHesapOnizle} disabled={loading}>
                            DK önizle
                        </Button>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={dkAktar}
                            disabled={loading || !seciliDosya}
                        >
                            Onaylı DK aktar
                        </Button>
                        {seciliDosya ? (
                            <Link
                                to={`/yeni/deger-kaybi-gelismis?dosya=${seciliDosya.id}&ekspertiz=1`}
                                className="inline-flex items-center h-9 px-3 rounded-md text-sm border border-violet-500/40 text-violet-200 hover:bg-violet-500/10"
                            >
                                Gelişmiş DK&apos;ya aktar →
                            </Link>
                        ) : null}
                        {dkOnizle?.gercek_deger_kaybi != null ? (
                            <span className="text-sm text-emerald-300 self-center">
                                DK: {tl(dkOnizle.gercek_deger_kaybi)}
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {tab === "sunum" ? (
                <div className="space-y-6">
                    <div className="flex flex-wrap gap-3">
                        <Button
                            className="bg-[#D4AF37] text-black"
                            onClick={sunumOlustur}
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Sparkles className="h-4 w-4 mr-2" />
                            )}
                            Sunum raporu oluştur
                        </Button>
                        {sunum?.sunum_metin ? (
                            <Button
                                variant="outline"
                                onClick={() => kopyala(sunum.sunum_metin, "Rapor")}
                            >
                                <Copy className="h-4 w-4 mr-2" />
                                Tüm raporu kopyala
                            </Button>
                        ) : null}
                    </div>
                    {sunum ? (
                        <>
                            <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5">
                                <h2 className="text-lg text-[#D4AF37] font-display">
                                    {sunum.rapor_baslik || sunum.sunum?.kapak_baslik}
                                </h2>
                                <p className="text-xs text-slate-500 mt-1">{sunum.olusturma}</p>
                            </div>
                            {sunum.sunum ? (
                                <div className="space-y-4">
                                    <Section title="Yönetici özeti" text={sunum.sunum.yonetici_ozeti} />
                                    <Section title="Dosya" text={sunum.sunum.dosya_ozeti} />
                                    <Section title="Belge durumu" text={sunum.sunum.belge_durumu} />
                                    <ListSection title="Net farklar" items={sunum.sunum.net_farklar} />
                                    <ListSection
                                        title="Kritik bulgular"
                                        items={sunum.sunum.kritik_bulgular}
                                        kritik
                                    />
                                    <Section
                                        title="Piyasa / web"
                                        text={sunum.sunum.piyasa_degerlendirmesi}
                                    />
                                    <ListSection
                                        title="Önerilen aksiyonlar"
                                        items={sunum.sunum.onerilen_aksiyonlar}
                                    />
                                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
                                        <div className="text-[10px] uppercase text-emerald-400">
                                            Sonuç
                                        </div>
                                        <p className="text-white mt-1">{sunum.sunum.sonuc_cumlesi}</p>
                                    </div>
                                </div>
                            ) : null}
                            <pre className="text-xs text-slate-400 bg-black/30 p-4 rounded-xl overflow-x-auto max-h-64 whitespace-pre-wrap">
                                {sunum.sunum_metin}
                            </pre>
                        </>
                    ) : (
                        <p className="text-slate-500 text-sm text-center py-16">
                            Belge yükleyip &quot;Sunum raporu oluştur&quot; ile hukuk bürosu formatında
                            rapor alın.
                        </p>
                    )}
                </div>
            ) : null}

            {tab === "adli" ? (
                <div className="space-y-6">
                    <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-5">
                        <h3 className="text-white font-medium flex items-center gap-2">
                            <Gavel className="h-5 w-5 text-rose-300" />
                            Adli Paket — tek tık tam analiz
                        </h3>
                        <p className="text-sm text-slate-400 mt-2">
                            Dosya + ekspertiz + fatura → web istihbarat + net fark tablosu + sunum
                            raporu + DK önizleme. Rapor adı otomatik atanır.
                        </p>
                        <Button
                            className="mt-4 bg-rose-600 hover:bg-rose-700"
                            onClick={adliPaketCalistir}
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Gavel className="h-4 w-4 mr-2" />
                            )}
                            Adli paketi çalıştır
                        </Button>
                    </div>
                    {adliPaket ? (
                        <div className="space-y-4">
                            <Kpi
                                label="Paket"
                                value={adliPaket.paket_baslik}
                                hint={adliPaket.mesaj}
                                accent="emerald"
                            />
                            {adliPaket.dosya ? (
                                <Link
                                    to={adliPaket.dosya.link}
                                    className="text-[#D4AF37] text-sm hover:underline"
                                >
                                    {adliPaket.dosya.dosya_no} · {adliPaket.dosya.plaka}
                                </Link>
                            ) : null}
                            {adliPaket.sunum_raporu?.sunum_metin ? (
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        kopyala(
                                            adliPaket.sunum_raporu.sunum_metin,
                                            "Adli paket"
                                        )
                                    }
                                >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Paket raporunu kopyala
                                </Button>
                            ) : null}
                            {adliPaket.deger_kaybi_onizleme?.gercek_deger_kaybi != null ? (
                                <p className="text-sm text-emerald-300">
                                    DK önizleme:{" "}
                                    {tl(adliPaket.deger_kaybi_onizleme.gercek_deger_kaybi)}
                                </p>
                            ) : null}
                        </div>
                    ) : (
                        <p className="text-slate-500 text-sm">
                            Dosya ve belgeleri hazırlayıp paketi çalıştırın.
                        </p>
                    )}
                </div>
            ) : null}

            {tab === "dosyalar" ? (
                <div className="grid lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1 space-y-2 max-h-[32rem] overflow-y-auto">
                        {dosyalar.map((d) => (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => dosyaSec(d)}
                                className={`w-full text-left rounded-lg border p-3 text-sm ${
                                    seciliDosya?.id === d.id
                                        ? "border-[#D4AF37]/50 bg-[#D4AF37]/10"
                                        : "border-white/10 hover:border-white/20"
                                }`}
                            >
                                <div className="text-white font-medium">{d.dosya_no}</div>
                                <div className="text-xs text-slate-500">
                                    {d.marka} {d.model}
                                </div>
                            </button>
                        ))}
                    </div>
                    <div className="lg:col-span-2">
                        {seciliDosya ? (
                            <div className="rounded-xl border border-white/10 p-4 space-y-4">
                                <h3 className="text-white font-medium">{seciliDosya.dosya_no}</h3>
                                <div className="text-sm text-slate-400">
                                    {seciliDosya.marka} {seciliDosya.model} · Rayiç{" "}
                                    {tl(seciliDosya.arac_rayic_degeri)}
                                </div>
                                {(seciliDosya.evraklar || []).map((e) => (
                                    <div
                                        key={e.id}
                                        className="flex justify-between py-2 border-b border-white/5 text-sm"
                                    >
                                        <span>{e.evrak_label}</span>
                                        {e.analiz_uygun ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs"
                                                onClick={() =>
                                                    evrakAnaliz(
                                                        e.id,
                                                        e.evrak_turu === "EKSPER"
                                                            ? "ekspertiz_raporu"
                                                            : "hasar_faturasi"
                                                    )
                                                }
                                            >
                                                Analiz
                                            </Button>
                                        ) : null}
                                    </div>
                                ))}
                                <Button
                                    size="sm"
                                    className="bg-rose-600/80"
                                    onClick={() => {
                                        setTab("adli");
                                        adliPaketCalistir();
                                    }}
                                    disabled={loading}
                                >
                                    Bu dosya için adli paket
                                </Button>
                            </div>
                        ) : (
                            <p className="text-slate-500 text-sm">Dosya seçin.</p>
                        )}
                    </div>
                </div>
            ) : null}

            {tab === "whatsapp" ? (
                <div className="rounded-xl border border-white/10 overflow-hidden">
                    <div className="px-4 py-3 border-b text-sm text-slate-300 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        WhatsApp medya — hasar belge adayları
                    </div>
                    <div className="divide-y divide-white/5 max-h-[28rem] overflow-y-auto">
                        {waKuyruk.map((w) => (
                            <div key={w.id} className="p-4 text-sm">
                                <span className="text-slate-300">{w.telefon}</span>
                                {w.dosya_no ? (
                                    <span className="text-slate-500 ml-2">· {w.dosya_no}</span>
                                ) : null}
                                <p className="text-xs text-slate-500 mt-1">{w.mesaj || "(medya)"}</p>
                                {w.media_url ? (
                                    <a
                                        href={w.media_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#D4AF37] text-xs mt-1 inline-block"
                                    >
                                        Medyayı aç
                                    </a>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function Section({ title, text }) {
    if (!text) return null;
    return (
        <div className="rounded-lg border border-white/10 p-4">
            <div className="text-[10px] uppercase text-slate-500 mb-1">{title}</div>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{text}</p>
        </div>
    );
}

function ListSection({ title, items, kritik }) {
    if (!items?.length) return null;
    return (
        <div
            className={`rounded-lg border p-4 ${
                kritik ? "border-rose-500/30 bg-rose-500/5" : "border-white/10"
            }`}
        >
            <div className="text-[10px] uppercase text-slate-500 mb-2">{title}</div>
            <ul className="text-sm text-slate-300 space-y-1">
                {items.map((x, i) => (
                    <li key={i}>• {x}</li>
                ))}
            </ul>
        </div>
    );
}
